import { DEFAULT_CATS, DEFAULT_SAVINGS_CATS } from '@/constants';
import { uid, todayStr } from '@/utils';
export const defaultProfile = { id: uid(), name: 'Пользователь', avatar: '🙂' };
export const initState = {
    budgets: [],
    currentId: null,
    view: 'home',
    budgetView: 'calendar',
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
    selectedDate: null,
    calMode: 'month',
    profile: defaultProfile,
};
export function reducer(state, a) {
    switch (a.type) {
        case 'CREATE_BUDGET': {
            const savingsEnabled = a.savingsEnabled ?? false;
            // Каждой seeded-категории — свежий GUID. DEFAULT_CATS имеют строковые id ('food'…),
            // а сервер синка ждёт Guid: без этого новый бюджет при import в облако снова плодит
            // дубли (локальная строка + серверный GUID) и malformed-операции.
            const cats = (savingsEnabled ? [...DEFAULT_CATS, ...DEFAULT_SAVINGS_CATS] : DEFAULT_CATS)
                .map(c => ({ ...c, id: uid() }));
            const b = {
                id: uid(),
                name: a.name,
                currency: a.currency,
                weekStart: a.weekStart,
                icon: a.icon || '💼',
                savingsEnabled,
                showCurrentBalance: a.showCurrentBalance ?? true,
                isArchived: false,
                categories: cats,
                transactions: a.startBalance > 0
                    ? [{ id: uid(), date: a.startDate || todayStr(), planned: a.startBalance, actual: a.startBalance, type: 'income', description: 'Начальный баланс', isStart: true }]
                    : [],
            };
            return { ...state, budgets: [...state.budgets, b] };
        }
        case 'UPDATE_BUDGET': {
            const budgets = state.budgets.map(b => b.id !== a.id ? b : { ...b, ...a.updates });
            return { ...state, budgets };
        }
        case 'TOGGLE_ARCHIVE_BUDGET': {
            const budgets = state.budgets.map(b => b.id !== a.id ? b : { ...b, isArchived: !b.isArchived });
            return { ...state, budgets };
        }
        case 'DELETE_BUDGET': {
            const budgets = state.budgets.filter(b => b.id !== a.id);
            const currentId = state.currentId === a.id ? null : state.currentId;
            return { ...state, budgets, currentId };
        }
        case 'OPEN_BUDGET':
            return { ...state, currentId: a.id, view: 'budget', budgetView: 'calendar', year: new Date().getFullYear(), month: new Date().getMonth(), selectedDate: todayStr(), calMode: 'month' };
        case 'GO_HOME':
            return { ...state, view: 'home', currentId: null, selectedDate: null };
        case 'GO_PROFILE':
            return { ...state, view: 'profile', currentId: null, selectedDate: null };
        case 'UPDATE_PROFILE':
            return { ...state, profile: { ...state.profile, ...a.updates } };
        case 'NAV_MONTH': {
            let { year, month } = state;
            month += a.delta;
            if (month > 11) {
                month = 0;
                year++;
            }
            if (month < 0) {
                month = 11;
                year--;
            }
            return { ...state, year, month, selectedDate: null };
        }
        case 'NAV_YEAR':
            return { ...state, year: state.year + a.delta, selectedDate: null };
        case 'GO_MONTH':
            return { ...state, year: a.year, month: a.month, calMode: 'month', selectedDate: null };
        case 'SET_MODE':
            return { ...state, calMode: a.mode, selectedDate: null };
        case 'SELECT_DATE':
            return { ...state, selectedDate: a.date };
        case 'ADD_TX': {
            const budgets = state.budgets.map(b => b.id !== state.currentId ? b : { ...b, transactions: [...b.transactions, { id: uid(), ...a.tx }] });
            return { ...state, budgets };
        }
        case 'UPDATE_TX': {
            const budgets = state.budgets.map(b => b.id !== state.currentId ? b : { ...b, transactions: b.transactions.map(tx => tx.id !== a.id ? tx : { ...tx, ...a.updates }) });
            return { ...state, budgets };
        }
        case 'DELETE_TX': {
            const budgets = state.budgets.map(b => b.id !== state.currentId ? b : { ...b, transactions: b.transactions.filter(tx => tx.id !== a.id) });
            return { ...state, budgets };
        }
        case 'UPDATE_SERIES_FROM': {
            const budgets = state.budgets.map(b => b.id !== state.currentId ? b : {
                ...b,
                transactions: b.transactions.map(tx => tx.seriesId === a.seriesId && tx.date >= a.fromDate ? { ...tx, ...a.updates } : tx),
            });
            return { ...state, budgets };
        }
        case 'DELETE_SERIES_FROM': {
            const budgets = state.budgets.map(b => b.id !== state.currentId ? b : {
                ...b,
                transactions: b.transactions.filter(tx => !(tx.seriesId === a.seriesId && tx.date >= a.fromDate)),
            });
            return { ...state, budgets };
        }
        case 'EXTEND_SERIES': {
            const budgets = state.budgets.map(b => b.id !== state.currentId ? b : {
                ...b,
                transactions: [...b.transactions, ...a.txs.map(tx => ({ id: uid(), ...tx }))],
            });
            return { ...state, budgets };
        }
        case 'SET_BUDGET_VIEW':
            return { ...state, view: a.view === 'dashboard' ? 'dashboard' : 'budget', budgetView: a.view };
        case 'SET_CURRENT_BUDGET':
            return state.currentId === a.id ? state : { ...state, currentId: a.id };
        case 'REPLACE_BUDGET_ID': {
            const budgets = state.budgets.map(b => b.id !== a.oldId ? b : { ...b, id: a.newId, ...a.updates });
            const currentId = state.currentId === a.oldId ? a.newId : state.currentId;
            return { ...state, budgets, currentId };
        }
        case 'INIT_DATA':
            return { ...state, budgets: a.budgets, profile: a.profile };
        case 'IMPORT_STATEMENT': {
            const updateMap = new Map(a.updates.map((u) => [u.id, u.updates]));
            const budgets = state.budgets.map((b) => {
                if (b.id !== a.budgetId)
                    return b;
                const merged = b.transactions.map((tx) => {
                    const upd = updateMap.get(tx.id);
                    return upd ? { ...tx, ...upd } : tx;
                });
                const added = a.newTxs.map((tx) => ({ id: uid(), ...tx }));
                return { ...b, transactions: [...merged, ...added] };
            });
            return { ...state, budgets };
        }
        case 'CLEAR_LOCAL_DATA':
            return { ...state, budgets: [], currentId: null, selectedDate: null };
        default:
            return state;
    }
}
